// F7：NVR / station 硬碟、獨立攝影機 SD 的本地錄影回放（app/home-base docs/KINIO-API.md §7、ADR-004 §2.6）。
//
// - 索引（有錄影的日子、時間軸、單段 URL）走設備既有 RPC：POST …/things/{device}/commands {op:"rpc", id:<method>, payload:<params>}
// - 影像資料走 lsrtc 回放隧道：同一條 signaling 的 start_playback，offer **只開 DataChannel**；
//   隧道上一次一個請求（設備序列處理，訊息沒有 request id）：
//     → "GET <path?query>" 或 "GET <path?query> bytes=<off>-<end>"   文字一行，≤ 512 bytes
//     ← "<status> <length> <content-type>"                           文字一則
//     ← <body…>                                                      零或多則二進位，每則 ≤ 16 KB
// - 目標是設備本體（不是通道子設備；獨立攝影機就是它自己，見 localPlaybackRef）；路徑白名單只有 /api/playback/hls/ 與 /api/playback/file/；不送 token。

import { kinioFetch } from './auth';
import { planeBase } from './config';
import { KinioError } from './errors';
import { extractGrant } from './live';
import { sharedSignal, type IceCandidateFrame, type MediaSignal, type SignalErrorFrame } from './signal';

/** 設備 RPC（op:"rpc"）。回 result.payload.result；設備回非 ok 丟 KinioError。 */
export async function thingRpc<T = unknown>(
  apiEndpoint: string,
  homeId: string,
  device: string,
  method: string,
  params: Record<string, unknown> = {},
  waitSeconds = 15,
): Promise<T> {
  const url = `${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(device)}/commands`;
  const res = await kinioFetch<{ status?: string; result?: { status?: string; payload?: { result?: T; error?: unknown } } }>(url, {
    method: 'POST',
    body: { op: 'rpc', id: method, payload: params, wait_seconds: waitSeconds },
  });
  if (res?.status !== 'acked' || res.result?.status !== 'ok') {
    const detail = res?.result?.payload?.error ?? res?.result?.status ?? res?.status ?? 'unknown';
    throw new KinioError(502, 'RPC_FAILED', `${method}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
  }
  return res.result.payload?.result as T;
}

/** 本地錄影來源：aid = pb.days / pb.timeline / 回放隧道的對象（子設備的 parent，或獨立攝影機自己）、ch = 參數裡的通道。 */
export interface LocalPlaybackRef {
  aid: string;
  ch: number;
}

const CAMERA_TYPES = new Set(['CAM', 'CVD', 'CFL']);

function sdUsable(reported: unknown): boolean {
  const st = (reported as { kinio_storage?: unknown } | null | undefined)?.kinio_storage;
  if (!st || typeof st !== 'object') return false;
  const { sd_present: present, sd_status: status } = st as { sd_present?: unknown; sd_status?: unknown };
  const hasPresent = typeof present === 'boolean';
  const hasStatus = typeof status === 'number';
  if (!hasPresent && !hasStatus) return false;
  if (present === false || status === 5) return false; // 沒插卡
  // 1 正常 / 3 已滿停錄（卡上還有錄影）/ 沒報狀態 → 問；2 / 6 需要格式化、4 格式化中 → 不問；其他（卡片故障）→ 問，讀不到照實顯示
  return !hasStatus || ![2, 4, 6].includes(status as number);
}

/**
 * 這台有沒有本地錄影來源（SPEC #recording-53；共用案例 testdata/playback_local，照抄 kinio-app-core，iOS / Android 同一份規則）：
 *   - 子設備 `<parent>:ch<N>`（NVR / Station / 基地台的通道）→ { aid: parent, ch: N }（不看 reported）。
 *   - 獨立攝影機（type_code，或沒有時 AID 前綴，是 CAM / CVD / CFL）且自己的 shadow reported 的 `kinio_storage` 有 SD 卡、
 *     狀態正常或卡片故障 → { aid: 自己, ch: 0 }（例 ls-2505）。
 *   - 其餘（沒插卡、需要格式化、格式化中、沒有 SD 欄位、reported 還沒取到、NVR / Station / 基地台本體）→ null。
 */
export function localPlaybackRef(deviceRef: string, reported?: unknown, typeCode?: string | null): LocalPlaybackRef | null {
  const m = /^(.+):ch(\d+)$/.exec(deviceRef);
  if (m) return { aid: m[1], ch: Number(m[2]) };
  const code = (typeCode?.trim() || (/^([^-]{3})-/.exec(deviceRef)?.[1] ?? '')).toUpperCase();
  if (!CAMERA_TYPES.has(code)) return null;
  return sdUsable(reported) ? { aid: deviceRef, ch: 0 } : null;
}

/** 本地錄影的一段（秒，epoch）。types：設備的錄影類型位元（1 連續、2 事件…，依 nvrd playback.c）。 */
export interface LocalSegment {
  start: number;
  end: number;
  types: number;
}

/** pb.days：某月有錄影的日子（YYYY-MM-DD）。 */
export function localRecordingDays(apiEndpoint: string, homeId: string, device: string, month: string, ch?: number): Promise<string[]> {
  return thingRpc<string[]>(apiEndpoint, homeId, device, 'pb.days', ch === undefined ? { month } : { month, ch });
}

/** pb.timeline：某天各通道的錄影區段 `{ "<ch>": LocalSegment[] }`。 */
export async function localTimeline(apiEndpoint: string, homeId: string, device: string, date: string, ch?: number): Promise<Record<string, LocalSegment[]>> {
  const r = await thingRpc<Record<string, LocalSegment[]>>(apiEndpoint, homeId, device, 'pb.timeline', ch === undefined ? { date } : { date, ch });
  return r && typeof r === 'object' ? r : {};
}

/** pb.thumb 的寬度：預設 160、上限 320（SPEC #vms-web-timeline）。 */
export const LOCAL_THUMB_WIDTH = 160;
export const LOCAL_THUMB_MAX_WIDTH = 320;

/**
 * pb.thumb：本地錄影（NVR / Station）最接近 t 的關鍵幀縮圖（SPEC #vms-web-timeline，PLAY-01 拖曳縮圖）。
 * 設備回 base64 JPEG（≤ 32 KB）；這裡包成 data URL。那個時間沒有錄影 → 設備回 NO_RECORDING（KinioError RPC_FAILED）。
 */
export async function localThumb(apiEndpoint: string, homeId: string, device: string, ch: number, t: number, w = LOCAL_THUMB_WIDTH): Promise<string> {
  const width = Math.min(Math.max(1, Math.round(w)), LOCAL_THUMB_MAX_WIDTH);
  const b64 = await thingRpc<string>(apiEndpoint, homeId, device, 'pb.thumb', { ch, t: Math.floor(t), w: width });
  if (typeof b64 !== 'string' || !b64) throw new KinioError(502, 'RPC_FAILED', 'pb.thumb: empty');
  return `data:image/jpeg;base64,${b64}`;
}

/** 回放 HLS 播放清單的路徑（nvrd playback.c：/api/playback/hls/<ch>/<stream>.m3u8?start=&end=）。 */
export function localPlaylistPath(ch: number, startSec: number, endSec: number, stream = 0): string {
  return `/api/playback/hls/${ch}/${stream}.m3u8?start=${startSec.toFixed(3)}&end=${endSec.toFixed(3)}`;
}

export interface TunnelResponse {
  status: number;
  /** header 宣告的長度；-1 = 沒給 */
  length: number;
  contentType: string;
  body: Uint8Array;
}

export interface PlaybackTunnelOptions {
  apiEndpoint: string;
  homeId: string;
  /** 設備本體的 device_ref（不是通道的 `<parent>:chN`）。 */
  deviceRef: string;
  /**
   * 要看的通道（`<主機>:ch<n>` 的 n，0 起算、上限 255）：放進 `start_playback` 的 `payload.channel`，平台只拿來記稽核
   * （`recording.playback` / `media.session.end` 的 resource `channel`），不轉給設備、不影響授權——隧道照舊對主機整顆磁碟。
   * 不帶 = 稽核只寫主機（舊行為）；不是 0～255 的整數也不帶（SPEC #vms-web-817「讀取不記成設備命令」，platform 2571097e）。
   * 獨立攝影機（隧道對象就是攝影機自己）不帶。
   */
  channel?: number;
  /**
   * 播放起點：放進 `start_playback` 的 `payload.from`（RFC 3339，UTC），平台只記進稽核的 `detail.from`
   * （「看了 NVR631 / CH4 21:29 起的本地錄影」），不轉給設備、不影響授權。只記開隧道時的起點，隧道裡換時間點不記——
   * 要記新的起點就關掉重開。數字 = epoch 秒（同 `LocalSegment.start`）；Date / RFC 3339 字串照收；不帶 = 不記
   * （SPEC #vms-web-817 第十二輪 B12-04，platform ded73195）。
   */
  from?: Date | number | string;
  signal?: MediaSignal;
  PeerConnectionImpl?: typeof RTCPeerConnection;
  onState?: (s: 'opening' | 'open' | 'failed' | 'closed') => void;
}

/** payload.channel 收的上限（同平台 internal/media hub.go maxViewCh）。 */
export const PLAYBACK_MAX_CHANNEL = 255;

/**
 * start_playback 的 payload.from：epoch 秒 / Date → RFC 3339 UTC（`2026-10-15T13:29:00.000Z`）；字串去頭尾空白照送（平台驗格式與範圍，
 * 不合就當沒帶）；不是有限數字、無效日期、空字串 = undefined（不帶）。
 */
export function playbackFromValue(from: unknown): string | undefined {
  if (from instanceof Date) return Number.isFinite(from.getTime()) ? from.toISOString() : undefined;
  if (typeof from === 'number') return Number.isFinite(from) ? new Date(from * 1000).toISOString() : undefined;
  if (typeof from === 'string') return from.trim() || undefined;
  return undefined;
}

/** 通道號能不能放進 start_playback 的 payload.channel（0～255 的整數）。 */
export function playbackChannelOk(ch: unknown): ch is number {
  return typeof ch === 'number' && Number.isInteger(ch) && ch >= 0 && ch <= PLAYBACK_MAX_CHANNEL;
}

interface Pending {
  line: string;
  resolve: (r: TunnelResponse) => void;
  reject: (e: unknown) => void;
  timeoutMs: number;
}

let tunnelSeq = 0;

/** 一條回放隧道：open() 後用 request() 一次一個地取播放清單與 byte-range 片段。 */
export class PlaybackTunnel {
  private readonly opts: PlaybackTunnelOptions;
  private readonly sig: MediaSignal;
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private sessionId: string | null = null;
  private offs: Array<() => void> = [];
  private pendingIce: (RTCIceCandidateInit | null)[] = [];
  private queue: Pending[] = [];
  private busy = false;
  private closed = false;
  // 目前這個請求的接收狀態
  private cur: { p: Pending; header: TunnelResponse | null; chunks: Uint8Array[]; got: number; timer: ReturnType<typeof setTimeout> } | null = null;

  constructor(opts: PlaybackTunnelOptions) {
    this.opts = opts;
    this.sig = opts.signal ?? sharedSignal(opts.apiEndpoint, opts.homeId);
  }

  async open(timeoutMs = 30_000): Promise<void> {
    const PC = this.opts.PeerConnectionImpl ?? (globalThis.RTCPeerConnection as typeof RTCPeerConnection);
    if (!PC) throw new KinioError(0, 'NO_WEBRTC', 'RTCPeerConnection unavailable');
    this.opts.onState?.('opening');
    await this.sig.ready();
    const pc = new PC();
    this.pc = pc;
    const dc = pc.createDataChannel('kinio-pb');
    dc.binaryType = 'arraybuffer';
    this.dc = dc;
    dc.onmessage = (ev) => this.onMessage(ev.data);
    dc.onclose = () => this.failAll(new KinioError(0, 'TUNNEL_CLOSED', 'playback tunnel closed'));
    pc.onicecandidate = (ev) => {
      const c = ev.candidate ? ev.candidate.toJSON() : null;
      if (this.sessionId) this.sig.sendIce(this.opts.deviceRef, this.sessionId, c);
      else this.pendingIce.push(c);
    };
    this.offs.push(
      this.sig.on('ice_candidate', (f: IceCandidateFrame) => {
        if (f.device_ref && f.device_ref !== this.opts.deviceRef) return;
        if (f.session_id && this.sessionId && f.session_id !== this.sessionId) return;
        if (f.candidate) void pc.addIceCandidate(f.candidate).catch(() => {});
      }),
      this.sig.on('error', (f: SignalErrorFrame) => {
        if (f.session_id && f.session_id === this.sessionId) {
          this.failAll(new KinioError(409, String(f.code ?? 'SIGNAL_ERROR'), String(f.message ?? '')));
          this.opts.onState?.('failed');
        }
      }),
    );
    const offer = await pc.createOffer();
    tunnelSeq += 1;
    const payload: Record<string, unknown> = {
      sdp: { type: 'offer', sdp: offer.sdp },
      connection_attempt_id: `pb-${Date.now().toString(36)}-${tunnelSeq}`,
    };
    if (playbackChannelOk(this.opts.channel)) payload.channel = this.opts.channel;
    const from = playbackFromValue(this.opts.from);
    if (from) payload.from = from;
    const resp = await this.sig.request(this.opts.deviceRef, 'start_playback', payload);
    const grant = extractGrant(resp.payload);
    this.sessionId = grant.sessionId;
    if (grant.iceServers.length) {
      try {
        pc.setConfiguration({ iceServers: grant.iceServers });
      } catch {
        /* 退回 host / srflx */
      }
    }
    await pc.setLocalDescription(offer);
    await pc.setRemoteDescription(grant.answer);
    for (const c of this.pendingIce.splice(0)) this.sig.sendIce(this.opts.deviceRef, this.sessionId, c);

    await new Promise<void>((resolve, reject) => {
      if (dc.readyState === 'open') return resolve();
      const t = setTimeout(() => reject(new KinioError(504, 'TUNNEL_TIMEOUT', 'playback data channel did not open')), timeoutMs);
      dc.onopen = () => {
        clearTimeout(t);
        resolve();
      };
    }).catch((e) => {
      this.opts.onState?.('failed');
      throw e;
    });
    this.opts.onState?.('open');
  }

  /** 送一行請求；前一個回應收完才送下一個（設備序列處理）。 */
  request(line: string, timeoutMs = 40_000): Promise<TunnelResponse> {
    if (this.closed) return Promise.reject(new KinioError(0, 'TUNNEL_CLOSED', 'playback tunnel closed'));
    if (line.length > 512) return Promise.reject(new KinioError(400, 'LINE_TOO_LONG', 'tunnel request line > 512 bytes'));
    return new Promise((resolve, reject) => {
      this.queue.push({ line, resolve, reject, timeoutMs });
      this.pump();
    });
  }

  private pump(): void {
    if (this.busy || !this.dc || this.dc.readyState !== 'open') return;
    const p = this.queue.shift();
    if (!p) return;
    this.busy = true;
    const timer = setTimeout(() => this.finish(new KinioError(504, 'TUNNEL_TIMEOUT', `no complete response for ${p.line}`)), p.timeoutMs);
    this.cur = { p, header: null, chunks: [], got: 0, timer };
    try {
      this.dc.send(p.line);
    } catch (e) {
      this.finish(e);
    }
  }

  private onMessage(data: unknown): void {
    const c = this.cur;
    if (!c) return;
    if (!c.header) {
      if (typeof data !== 'string') return this.finish(new KinioError(502, 'BAD_TUNNEL_HEADER', 'first message is binary'));
      const m = /^(\d+)\s+(-?\d+)\s*(\S*)/.exec(data.trim());
      if (!m) return this.finish(new KinioError(502, 'BAD_TUNNEL_HEADER', data.slice(0, 80)));
      c.header = { status: Number(m[1]), length: Number(m[2]), contentType: m[3] ?? '', body: new Uint8Array() };
      if (c.header.length === 0) return this.finish(null);
      return;
    }
    const chunk = typeof data === 'string' ? new TextEncoder().encode(data) : new Uint8Array(data as ArrayBuffer);
    c.chunks.push(chunk);
    c.got += chunk.byteLength;
    if (c.header.length >= 0 && c.got >= c.header.length) this.finish(null);
  }

  private finish(err: unknown): void {
    const c = this.cur;
    if (!c) return;
    clearTimeout(c.timer);
    this.cur = null;
    this.busy = false;
    if (err) c.p.reject(err);
    else {
      const body = new Uint8Array(c.got);
      let o = 0;
      for (const ch of c.chunks) {
        body.set(ch, o);
        o += ch.byteLength;
      }
      c.p.resolve({ ...(c.header as TunnelResponse), body });
    }
    this.pump();
  }

  private failAll(e: unknown): void {
    // 先拿走排隊的：finish() 會 pump() 下一個，以前排隊的請求因此被送上已經壞掉的隧道、掛到 timeout 才結束
    const queued = this.queue.splice(0);
    if (this.cur) this.finish(e);
    for (const p of queued) p.reject(e);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    for (const off of this.offs.splice(0)) off();
    const sid = this.sessionId;
    this.sessionId = null;
    if (sid) this.sig.request(this.opts.deviceRef, 'stop_viewing', { session_id: sid }, 5000).catch(() => {});
    this.failAll(new KinioError(0, 'TUNNEL_CLOSED', 'playback tunnel closed'));
    try {
      this.dc?.close();
      this.pc?.close();
    } catch {
      /* already closed */
    }
    this.dc = null;
    this.pc = null;
    this.opts.onState?.('closed');
  }
}
